/* ============================================================================
   DISPOSICIÓN DEL MAPA

   Hoy el mapa ocupa el 70 % del alto y los controles lo empujan hacia abajo.
   En vuelo se mira el mapa y poco más: ese reparto desperdicia justo lo que
   más importa.

   ══════════════════════════════════════════════════════════════════════════
   NO SE APLICA A TODA LA APLICACIÓN, Y ES A PROPÓSITO

   El peso y balance, el libro de vuelo, el SGS y el duty time son pantallas de
   FORMULARIO: veinte campos que se llenan mirando. Ponerlos como capas
   flotantes sobre un mapa los empeora.

   Copiar el patrón donde no corresponde cambia una molestia por otra.

   ══════════════════════════════════════════════════════════════════════════
   Y HAY UN TAMAÑO POR DEBAJO DEL CUAL LAS CAPAS SON PEORES

   En una pantalla chica —un iPad en vertical, un teléfono— una capa flotante
   tapa medio mapa. Ahí es mejor el apilado de siempre: se ve menos mapa, pero
   se ve entero.

   Un piloto que tiene que mover paneles para ver dónde está es un piloto
   mirando la pantalla en vez de afuera.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

/** Las pestañas donde el mapa es lo principal. */
export const PESTAÑAS_DE_MAPA = ["ruta", "vuelo"];

/**
 * Debajo de esto, las capas flotantes tapan más de lo que aportan.
 *
 * 820 px es un iPad en horizontal; en vertical son 768 y cae del lado del
 * apilado, que es lo correcto: en vertical no hay ancho para poner nada al
 * costado sin comerse el mapa.
 */
export const ANCHO_MINIMO_CAPAS = 820;

/** Y debajo de este alto, el mapa a pantalla completa deja los controles sin lugar. */
export const ALTO_MINIMO_CAPAS = 520;

/**
 * ¿Esta pestaña usa el mapa a pantalla completa?
 */
export function esPestañaDeMapa(pestaña) {
  return PESTAÑAS_DE_MAPA.includes(String(pestaña || ""));
}

/**
 * Cómo se dispone la pantalla.
 *
 * @param {string} pestaña
 * @param {{ancho:number, alto:number}} ventana
 */
export function disposicion(pestaña, ventana = {}) {
  const ancho = Number(ventana.ancho);
  const alto = Number(ventana.alto);

  if (!esPestañaDeMapa(pestaña)) {
    return {
      modo: "apilado",
      motivo: "Es una pantalla de formulario: los campos se leen mejor en "
        + "columna que flotando sobre un mapa.",
    };
  }

  /* Sin medidas no se arriesga: el apilado funciona en todos lados, y las
     capas flotantes en una pantalla que no las aguanta tapan el mapa. */
  if (!Number.isFinite(ancho) || !Number.isFinite(alto)) {
    return { modo: "apilado", motivo: "Sin medidas de la ventana." };
  }

  if (ancho < ANCHO_MINIMO_CAPAS || alto < ALTO_MINIMO_CAPAS) {
    return {
      modo: "apilado",
      motivo: `La pantalla es de ${Math.round(ancho)}×${Math.round(alto)}: `
        + "una capa flotante taparía más mapa del que deja ver.",
    };
  }

  return { modo: "capas", motivo: null };
}

/** Los paneles que flotan sobre el mapa, con su lugar. */
export const PANELES_FLOTANTES = [
  {
    clave: "ruta",
    titulo: "Ruta",
    lado: "izquierda",
    /* La ruta se abre por defecto: es lo que el piloto viene a armar. */
    abiertoPorDefecto: true,
  },
  {
    clave: "meteo",
    titulo: "Meteorología",
    lado: "derecha",
    abiertoPorDefecto: false,
  },
  {
    clave: "capas",
    titulo: "Capas",
    lado: "derecha",
    abiertoPorDefecto: false,
  },
];

/** Dónde se recuerda qué paneles dejó abiertos el piloto. */
export const CLAVE_PANELES = "hcPanelesFlotantes";

export function panelesAbiertos() {
  const porDefecto = Object.fromEntries(
    PANELES_FLOTANTES.map((p) => [p.clave, p.abiertoPorDefecto]),
  );

  try {
    const guardado = JSON.parse(localStorage.getItem(CLAVE_PANELES) || "null");
    if (!guardado || typeof guardado !== "object") return porDefecto;
    /* Se fusiona con los valores por defecto: un panel nuevo tiene que
       aparecer aunque el piloto tenga una preferencia vieja guardada. */
    return { ...porDefecto, ...guardado };
  } catch { return porDefecto; }
}

export function guardarPaneles(estado) {
  try { localStorage.setItem(CLAVE_PANELES, JSON.stringify(estado || {})); } catch { /* modo privado */ }
}

/**
 * ¿Se puede cerrar este panel?
 *
 * Los avisos permanentes —que esto no es equipo de navegación, que hay una
 * alerta de espacio aéreo— **no se pueden plegar**. Un aviso que se puede
 * hacer desaparecer es un aviso en el que no se puede confiar, y esa regla ya
 * está en el resto de la aplicación.
 */
export function sePuedePlegar(clave) {
  return PANELES_FLOTANTES.some((p) => p.clave === clave);
}

/**
 * El alto que le queda al mapa.
 *
 * Se descuenta la cabecera y la barra de pestañas: un mapa que se mete debajo
 * de la cabecera deja la esquina inaccesible, y ahí es donde están los
 * controles de zoom.
 */
export function altoDelMapa({ alto, cabeceraPx = 0, pestañasPx = 0 } = {}) {
  const h = Number(alto);
  if (!Number.isFinite(h)) return null;

  const disponible = h - (Number(cabeceraPx) || 0) - (Number(pestañasPx) || 0);

  /* Nunca menos de 320 px: por debajo de eso el mapa no sirve ni para
     ubicarse, y es mejor que la página se desplace. */
  return Math.max(320, Math.round(disponible));
}
